import type { DatePrecision } from "./schemas.js";

/** Result of parsing a user-entered "pictured" date. The canonical `date`
 *  always has full YYYY-MM-DD form (using "01" placeholders for missing parts)
 *  so it can round-trip through a Postgres `date` column. */
export interface ParsedEventDate {
  date: string;
  precision: DatePrecision;
}

/**
 * Parse a user-entered date that may be a year, year-month, or full date.
 * Returns null on empty input. Throws on invalid input so callers can surface
 * a helpful error.
 *
 * Examples:
 *   "1953"        -> { date: "1953-01-01", precision: "year" }
 *   "1953-04"     -> { date: "1953-04-01", precision: "month" }
 *   "1953-04-07"  -> { date: "1953-04-07", precision: "day" }
 */
export function parseEventDateInput(
  raw: string | null | undefined,
): ParsedEventDate | null {
  if (raw === null || raw === undefined) return null;
  const trimmed = raw.trim();
  if (trimmed === "") return null;

  if (/^\d{4}$/.test(trimmed)) {
    return { date: `${trimmed}-01-01`, precision: "year" };
  }
  if (/^\d{4}-\d{2}$/.test(trimmed)) {
    return { date: `${trimmed}-01`, precision: "month" };
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return { date: trimmed, precision: "day" };
  }

  throw new Error("must be YYYY, YYYY-MM, or YYYY-MM-DD");
}

/**
 * Format a precision-aware event date for display.
 * Falls back to the raw string if parsing fails for any reason.
 */
export function formatEventDate(
  date: string | null | undefined,
  precision: DatePrecision = "day",
  locale?: string,
): string {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;

  switch (precision) {
    case "year":
      return d.toLocaleDateString(locale, { year: "numeric" });
    case "month":
      return d.toLocaleDateString(locale, { year: "numeric", month: "short" });
    case "day":
    default:
      return d.toLocaleDateString(locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
  }
}

/** Given a canonical YYYY-MM-DD date and precision, render the input string
 *  most useful as the initial value of a free-form input. */
export function eventDateInputValue(
  date: string | null | undefined,
  precision: DatePrecision = "day",
): string {
  if (!date) return "";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!match) return date;
  const [, year, month, day] = match;
  switch (precision) {
    case "year":
      return year!;
    case "month":
      return `${year}-${month}`;
    case "day":
    default:
      return `${year}-${month}-${day}`;
  }
}
